import type { Locale } from './locales';

/**
 * Plural categories, per language, as CLDR defines them.
 *
 * <p><strong>Slovenian has a dual.</strong> One truck is `1 sledilnik`, two are
 * `2 sledilnika`, three or four are `3 sledilniki`, five or more are `5 sledilnikov` —
 * and the dual is not an archaism, it is the everyday form. A `count === 1` ternary gets
 * three of those four wrong, and it gets them wrong at exactly the fleet sizes this
 * product has. Croatian has no dual but does have a separate few (2–4, and 22–24, and
 * 32–34…), so it is wrong in a different place.
 *
 * <p>Typing the categories per locale is the point of this file: a Slovenian entry that
 * forgets `two` does not compile. That is a guarantee a JSON-per-locale setup cannot
 * offer, and it is worth more here than the tooling it costs — a missing dual is not a
 * missing string, so nothing renders blank and nobody notices until a native speaker
 * reads the screen.
 */
export interface PluralCategories {
  en: 'one' | 'other';
  sl: 'one' | 'two' | 'few' | 'other';
  hr: 'one' | 'few' | 'other';
}

export type Forms<L extends Locale> = Record<PluralCategories[L], string>;

/**
 * Binds a plural chooser to one locale.
 *
 * <p>`Intl.PluralRules` is the CLDR data the browser already ships, so there is no rule
 * table to maintain and no chance of it disagreeing with the rest of the platform's
 * formatting. Each catalogue file makes one of these at the top and uses it throughout.
 *
 * <p>The returned function substitutes `{n}` with the count formatted for that same
 * locale — the separator matters even here, because a fleet of 1 200 renders `1.200` in
 * Slovenian and `1,200` in English.
 */
export function pluralFor<L extends Locale>(locale: L) {
  const rules = new Intl.PluralRules(locale);
  const numbers = new Intl.NumberFormat(locale);

  return (count: number, forms: Forms<L>): string => {
    const category = rules.select(count) as PluralCategories[L];
    // `other` is the CLDR fallback and every locale has it, so an unexpected category
    // (a browser with newer CLDR data than these types) degrades instead of throwing.
    const template = forms[category] ?? forms['other' as PluralCategories[L]];
    return template.replace('{n}', numbers.format(count));
  };
}

/**
 * Fills `{name}` placeholders.
 *
 * <p>Deliberately tiny and deliberately not a template literal in the catalogue: a
 * template literal would have to be a function of its arguments in every language, and
 * the whole reason the catalogue is data is that a translator can reorder a sentence.
 * Slovenian and English do not agree on where the subject goes.
 */
export function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}
